import { MGPFallible, MGPMap, MGPOptional, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Ordinal } from '../../jscaip/Ordinal';
import { Player } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { LodestoneFailure } from './LodestoneFailure';
import { LodestonePieceLodestone, LodestonePieceNone, LodestonePiecePlayer } from './LodestonePiece';
import { LodestoneState, LodestonePressurePlates } from './LodestoneState';
import { LodestonePressurePlate } from './LodestoneState';
export class LodestoneNode extends GameNode {
}
export class LodestoneRules extends Rules {
    static THREATENED_COORD_RANGE = MGPMap.from({
        top: {
            start: (indexPlate) => new Coord(0, indexPlate),
            direction: Ordinal.RIGHT,
        },
        bottom: {
            start: (indexPlate) => new Coord(0, LodestoneState.SIZE - (indexPlate + 1)),
            direction: Ordinal.RIGHT,
        },
        left: {
            start: (indexPlate) => new Coord(indexPlate, 0),
            direction: Ordinal.DOWN,
        },
        right: {
            start: (indexPlate) => new Coord(LodestoneState.SIZE - (indexPlate + 1), 0),
            direction: Ordinal.DOWN,
        },
    });
    static singleton = MGPOptional.empty();
    static get() {
        if (LodestoneRules.singleton.isAbsent()) {
            LodestoneRules.singleton = MGPOptional.of(new LodestoneRules());
        }
        return LodestoneRules.singleton.get();
    }
    getInitialState() {
        const _ = LodestonePieceNone.EMPTY;
        const O = LodestonePiecePlayer.ZERO;
        const X = LodestonePiecePlayer.ONE;
        const board = [
            [_, _, O, X, O, X, _, _],
            [_, O, X, O, X, O, X, _],
            [O, X, O, X, O, X, O, X],
            [X, O, X, _, _, O, X, O],
            [O, X, O, _, _, X, O, X],
            [X, O, X, O, X, O, X, O],
            [_, X, O, X, O, X, O, _],
            [_, _, X, O, X, O, _, _],
        ];
        const plates = LodestonePressurePlates.getInitialLodestonePressurePlates([5, 3]);
        return new LodestoneState(board, 0, new MGPMap(), plates);
    }
    applyLegalMove(move, state, _config, infos) {
        const currentPlayer = state.getCurrentPlayer();
        const opponent = currentPlayer.getOpponent();
        const board = TableUtils.copy(infos.board);
        const lodestones = state.lodestones.getCopy();
        lodestones.put(currentPlayer, move.coord);
        const pressurePlates = { ...state.pressurePlates };
        this.updatePressurePlates(board, pressurePlates, lodestones, opponent, move.captures);
        return new LodestoneState(board, state.turn + 1, lodestones, pressurePlates);
    }
    updatePressurePlates(board, pressurePlates, lodestones, opponent, captures) {
        for (const position of LodestonePressurePlate.POSITIONS) {
            pressurePlates[position] = this.updatePressurePlate(board, position, pressurePlates[position], lodestones, opponent, captures[position]);
        }
    }
    updatePressurePlate(board, position, pressurePlate, lodestones, opponent, captured) {
        if (pressurePlate.getCurrentPlate().isPresent()) {
            const newPressurePlate = pressurePlate.addCaptured(opponent, captured);
            const plateInfo = LodestoneRules.THREATENED_COORD_RANGE.get(position).get();
            const length = newPressurePlate.getCrumbledPlates().length;
            for (let plateIndex = 0; plateIndex < length; plateIndex++) {
                const plateWidth = newPressurePlate.plates[plateIndex].width;
                this.removePressurePlate(board, plateInfo.start(plateIndex, plateWidth), plateInfo.direction, lodestones);
            }
            return newPressurePlate;
        }
        else {
            return pressurePlate;
        }
    }
    removePressurePlate(board, start, direction, lodestones) {
        for (let coord = start; // eslint-disable-next-line indent
         coord.isInRange(LodestoneState.SIZE, LodestoneState.SIZE); // eslint-disable-next-line indent
         coord = coord.getNext(direction)) {
            for (const player of Player.PLAYERS) {
                if (lodestones.get(player).equalsValue(coord)) {
                    lodestones.delete(player);
                }
            }
            board[coord.y][coord.x] = LodestonePieceNone.UNREACHABLE;
        }
    }
    isLegal(move, state) {
        const validityBeforeCaptures = this.isLegalWithoutCaptures(state, move.coord, move.direction);
        if (validityBeforeCaptures.isFailure()) {
            return MGPFallible.failure(validityBeforeCaptures.getReason());
        }
        const infos = this.applyMoveWithoutPlacingCaptures(state, move.coord, move);
        const numberOfCapturesInMove = move.captures.top + move.captures.bottom + move.captures.left + move.captures.right;
        const actualCaptures = Math.min(infos.captures.length, state.remainingSpaces());
        if (numberOfCapturesInMove !== actualCaptures) {
            return MGPFallible.failure(LodestoneFailure.MUST_PLACE_CAPTURES_ON_PRESSURE_PLATES());
        }
        for (const position of LodestonePressurePlate.POSITIONS) {
            const pressurePlate = state.pressurePlates[position];
            if (pressurePlate.getGroupRemainingSpaces() < move.captures[position]) {
                return MGPFallible.failure(LodestoneFailure.TOO_MANY_CAPTURES_ON_SAME_PRESSURE_PLATE());
            }
        }
        return MGPFallible.success(infos);
    }
    applyMoveWithoutPlacingCaptures(state, coord, lodestone) {
        let result;
        const board = TableUtils.copy(state.board);
        const previousLodestonePosition = state.lodestones.get(state.getCurrentPlayer());
        if (previousLodestonePosition.isPresent()) {
            const previousCoord = previousLodestonePosition.get();
            board[previousCoord.y][previousCoord.x] = LodestonePieceNone.EMPTY;
        }
        if (lodestone.direction === 'pull') {
            result = this.applyPull(state, board, coord, lodestone.orientation);
        }
        else {
            result = this.applyPush(state, board, coord, lodestone.orientation);
        }
        result.board[coord.y][coord.x] =
            LodestonePieceLodestone.of(state.getCurrentPlayer(), lodestone);
        result.moved.push(coord);
        return result;
    }
    applyPull(state, board, lodestone, orientation) {
        const currentPlayer = state.getCurrentPlayer();
        const opponent = currentPlayer.getOpponent();
        const captures = [];
        const moved = [];
        const directions = orientation === 'diagonal' ? Ordinal.DIAGONALS : Ordinal.ORTHOGONALS;
        for (const direction of directions) {
            let coord = lodestone.getNext(direction);
            while (state.isOnBoard(coord)) {
                const pieceOnTarget = board[coord.y][coord.x];
                const next = coord.getNext(direction);
                if (state.coordIsOwnedBy(next, currentPlayer)) {
                    const pieceToMove = board[next.y][next.x];
                    if (pieceToMove.isPlayerPiece()) {
                        // We move player piece of the next coord to the current coord
                        // hence in the opposite of direction
                        if (pieceOnTarget.isEmpty()) {
                            // Moving to an empty square
                            moved.push(coord);
                            board[coord.y][coord.x] = pieceToMove;
                            moved.push(next);
                            board[next.y][next.x] = LodestonePieceNone.EMPTY;
                        }
                        else if (pieceOnTarget.isPlayerPiece() && pieceOnTarget.owner === opponent) {
                            // Capturing a player piece
                            captures.push(coord);
                            board[coord.y][coord.x] = pieceToMove;
                            moved.push(next);
                            board[next.y][next.x] = LodestonePieceNone.EMPTY;
                        }
                    }
                }
                coord = coord.getNext(direction);
            }
        }
        return { board, captures, moved };
    }
    applyPush(state, board, lodestone, orientation) {
        const currentPlayer = state.getCurrentPlayer();
        const opponent = currentPlayer.getOpponent();
        const captures = [];
        const moved = [];
        const directions = orientation === 'diagonal' ? Ordinal.DIAGONALS : Ordinal.ORTHOGONALS;
        for (const direction of directions) {
            const start = lodestone.getNext(direction, LodestoneState.SIZE);
            for (let coord = start; // eslint-disable-next-line indent
             coord.equals(lodestone) === false; // eslint-disable-next-line indent
             coord = coord.getPrevious(direction)) {
                if (state.coordIsOwnedBy(coord, opponent)) {
                    const pieceToMove = board[coord.y][coord.x];
                    if (pieceToMove.isPlayerPiece()) {
                        const next = coord.getNext(direction);
                        if (state.isOnBoard(next)) {
                            const pieceOnTarget = board[next.y][next.x];
                            if (pieceOnTarget.isUnreachable()) {
                                // Floor here has crumbled, the piece falls
                                captures.push(coord);
                                board[coord.y][coord.x] = LodestonePieceNone.EMPTY;
                            }
                            else if (pieceOnTarget.isEmpty()) {
                                // Moving to an empty square
                                moved.push(next);
                                board[next.y][next.x] = pieceToMove;
                                moved.push(coord);
                                board[coord.y][coord.x] = LodestonePieceNone.EMPTY;
                            }
                        }
                        else {
                            // Piece falls off the board
                            captures.push(coord);
                            board[coord.y][coord.x] = LodestonePieceNone.EMPTY;
                        }
                    }
                }
            }
        }
        return { board, captures, moved };
    }
    isLegalWithoutCaptures(state, coord, direction) {
        const targetValidity = this.isTargetLegal(state, coord);
        if (targetValidity.isFailure()) {
            return targetValidity;
        }
        const nextLodestoneDirection = state.nextLodestoneDirection();
        const validLodestoneDirection = nextLodestoneDirection.isAbsent() || nextLodestoneDirection.equalsValue(direction);
        if (validLodestoneDirection === false) {
            return MGPValidation.failure(LodestoneFailure.MUST_FLIP_LODESTONE());
        }
        return MGPValidation.SUCCESS;
    }
    isTargetLegal(state, coord) {
        const targetContent = state.getPieceAt(coord);
        if (targetContent.isUnreachable()) {
            return MGPValidation.failure(LodestoneFailure.TARGET_IS_CRUMBLED());
        }
        if (targetContent.isPlayerPiece()) {
            return MGPValidation.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SQUARE());
        }
        const player = state.getCurrentPlayer();
        if (targetContent.isLodestone() && targetContent.owner !== player) {
            return MGPValidation.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SQUARE());
        }
        return MGPValidation.SUCCESS;
    }
    getGameStatus(node) {
        const state = node.gameState;
        const pieces = state.numberOfPieces();
        const piecesZero = pieces.get(Player.ZERO);
        const piecesOne = pieces.get(Player.ONE);
        if (piecesZero === 0 && piecesOne === 0) {
            return GameStatus.DRAW;
        }
        else if (piecesZero === 0) {
            return GameStatus.ONE_WON;
        }
        else if (piecesOne === 0) {
            return GameStatus.ZERO_WON;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
}
